import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";

import { api, unwrap } from "@/api/client";
import { fetchSession, pathForStep, SESSION_KEY, useSession } from "@/auth/session";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { errorMessage } from "@/i18n";

import { AuthLayout } from "./auth-layout";

// The server enforces the real limit (PASSWORD_MIN_LENGTH); this only
// saves a round trip for the common case.
const MIN_LENGTH = 12;

const schema = z
  .object({
    currentPassword: z.string().min(1, "signIn.passwordRequired"),
    newPassword: z.string().min(MIN_LENGTH, "password.tooShort"),
    confirm: z.string(),
  })
  .refine((values) => values.newPassword === values.confirm, {
    path: ["confirm"],
    message: "password.mismatch",
  });
type Values = z.infer<typeof schema>;

export function ChangePasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const form = useForm<Values>({ resolver: zodResolver(schema) });
  const change = useMutation({
    mutationFn: async ({ currentPassword, newPassword }: Values) => {
      unwrap(await api.POST("/api/v1/auth/password", { body: { currentPassword, newPassword } }));
      return fetchSession();
    },
    onSuccess: (next) => {
      queryClient.setQueryData(SESSION_KEY, next);
      void navigate(next ? pathForStep(next.nextStep) : "/sign-in", { replace: true });
    },
  });
  const errors = form.formState.errors;
  const oneTime = session.data?.user.mustChangePassword ?? false;
  const message = (key: string | undefined) => (key ? t(key) : undefined);

  return (
    <AuthLayout
      title={t("password.title")}
      intro={oneTime ? t("password.introOneTime") : t("password.intro")}
    >
      <form
        noValidate
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          void form.handleSubmit((values) => {
            change.mutate(values);
          })(event);
        }}
      >
        {change.isError ? <Alert tone="error">{errorMessage(change.error)}</Alert> : null}
        <Field
          label={t("password.current")}
          type="password"
          autoComplete="current-password"
          error={message(errors.currentPassword?.message)}
          {...form.register("currentPassword")}
        />
        <Field
          label={t("password.new")}
          hint={t("password.newHint")}
          type="password"
          autoComplete="new-password"
          error={message(errors.newPassword?.message)}
          {...form.register("newPassword")}
        />
        <Field
          label={t("password.confirm")}
          type="password"
          autoComplete="new-password"
          error={message(errors.confirm?.message)}
          {...form.register("confirm")}
        />
        <Button type="submit" disabled={change.isPending}>
          {t("password.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
